import { useAuth0 } from '@auth0/auth0-react'
import styled from '@emotion/styled'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useAddUserMetadata } from '~/api/app/user-metadata.api'
import { useSendAlertEmail } from '~/api/app/user-send-alert-email.api'
import { Fonts } from '~/assets/style/fonts'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import Button from '~/components/Button'
import { ContentCard } from '~/components/ContentCard'
import { Icon } from '~/components/Icon'
import { Input } from '~/components/Input'
import { Toggle } from '~/components/Toggle'
import { SETTINGS_NAV_CONFIG } from '~/utils/settings'

const ProfileContentCard = styled(ContentCard)`
  display: flex;
  flex-direction: column;
  gap: 43px;
`

const ProfileContentCardCategory = styled.div``

const ProfileContentCardTitle = styled.h2`
  ${Fonts.colors.Jet}
  ${Fonts.H2}
  font-weight: 400;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 16px;
`
const ProfileUserInfo = styled.div`
  display: flex;
  gap: 20px;
  & > * {
    flex-grow: 1;
  }
`
const ProfileNotification = styled.div``

const ProfileNotificationSettings = styled.div`
  ${Fonts.P1}
  ${Fonts.colors.Jet}
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 32px;
`

export const SettingsProfilePage = () => {
  const user = useAuthenticatedUser()
  const { mutateAsync: sendAlertEmail } = useSendAlertEmail()
  const { mutateAsync: addUserMetadata } = useAddUserMetadata()
  const { getAccessTokenSilently } = useAuth0()

  const [hasEmailNotifications, setHasEmailNotifications] = useState<boolean>(user.alertEmail?.[user.currentScopeId] ?? false)

  const userInfo = { name: user.profile.nickname ?? '', email: user.profile.email ?? '', role: user.getScopeRole() ?? 'user' }
  const onTestEmailSend = async () => {
    await sendAlertEmail()
  }

  const onToggleEmailNotifications = async () => {
    if (user) {
      setHasEmailNotifications(!hasEmailNotifications)
      await addUserMetadata({
        scopeId: user.currentScopeId,
        alertEmail: { ...user.alertEmail, [user.currentScopeId]: !hasEmailNotifications },
      })

      await getAccessTokenSilently({ cacheMode: 'off' }) //TODO tmu nra 2022-10-13 find out why it reloads on firefox
    }
  }

  const { control } = useForm({
    defaultValues: {
      ...userInfo,
    },
  })

  return (
    <ProfileContentCard color={SETTINGS_NAV_CONFIG['profile'].color}>
      <ProfileContentCardCategory>
        <ProfileContentCardTitle>
          <Icon name={'account_circle'} width={'22px'} />
          Personal information
        </ProfileContentCardTitle>
        <ProfileUserInfo>
          <Input disabled label={'Name'} control={control} name={'name'} />
          <Input disabled label={'E-mail address'} control={control} name={'email'} />
          <Input disabled label={'Role'} control={control} name={'role'} />
        </ProfileUserInfo>
      </ProfileContentCardCategory>
      <ProfileContentCardCategory>
        <ProfileNotification>
          <ProfileContentCardTitle>
            <Icon name={'notification'} width={'22px'} />
            Notifications
          </ProfileContentCardTitle>
          <ProfileNotificationSettings>
            <Toggle
              onClick={onToggleEmailNotifications}
              value={hasEmailNotifications}
              trackingLabel={hasEmailNotifications ? 'common-settings-profile-email-notifications-unset' : 'common-settings-profile-email-notifications-set'}
            />{' '}
            Receive notifications by e-mail
          </ProfileNotificationSettings>
          <Button variant={'secondary'} iconName={'mail'} onClick={onTestEmailSend} trackingLabel={'common-settings-profile-sending-test-email-apply'}>
            Send test e-mail
          </Button>
        </ProfileNotification>
      </ProfileContentCardCategory>
    </ProfileContentCard>
  )
}
